import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Chip, Switch, FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import GpsFixedIcon from '@material-ui/icons/GpsFixed';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { AuthContext } from '../../App';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
    backdropFilter: 'blur(8px)', zIndex: 1300,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  dialog: {
    background: '#fff', borderRadius: 16, width: 560, maxWidth: '95vw',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: 'linear-gradient(180deg, #fff 0%, #dbeafe 100%)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  content: { flex: 1, overflowY: 'auto', padding: '12px 20px' },
  card: {
    border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 8,
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  },
}));

function GeofenceDialog({ open, onClose, lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [geofences, setGeofences] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', lat: '', lng: '', radius: '1000', color: '#3b82f6', alertOnEntry: true, alertOnExit: true });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  async function fetchData() {
    if (!token) return;
    const [gRes, aRes] = await Promise.all([
      fetch('/planner-api/geofences', { headers }),
      fetch('/planner-api/geofences/alerts', { headers }),
    ]);
    setGeofences(await gRes.json());
    setAlerts(await aRes.json());
  }

  useEffect(() => { if (open) fetchData(); }, [open, token]);

  async function handleCreate() {
    if (!form.name || !form.lat || !form.lng) return;
    await fetch('/planner-api/geofences', {
      method: 'POST', headers,
      body: JSON.stringify({ ...form, lat: Number(form.lat), lng: Number(form.lng), radius: Number(form.radius) }),
    });
    setShowForm(false);
    setForm({ name: '', lat: '', lng: '', radius: '1000', color: '#3b82f6', alertOnEntry: true, alertOnExit: true });
    fetchData();
  }

  async function handleDelete(id) {
    await fetch(`/planner-api/geofences/${id}`, { method: 'DELETE', headers });
    fetchData();
  }

  if (!open) return null;

  return (
    <Box className={classes.overlay} onClick={onClose}>
      <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
        <Box className={classes.header}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <GpsFixedIcon style={{ fontSize: 20, color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
              Geofencing
            </Typography>
            <Chip label={geofences.length} size="small"
              style={{ height: 20, fontSize: '0.6rem', fontWeight: 700, background: '#dbeafe', color: '#2563eb' }} />
          </Box>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px' }}>
            {pt ? 'Fechar' : 'Close'}
          </Button>
        </Box>

        <Box className={classes.content}>
          {/* Recent alerts */}
          {alerts.length > 0 && (
            <Box style={{ marginBottom: 16 }}>
              <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                {pt ? 'Alertas recentes (24h)' : 'Recent alerts (24h)'} ({alerts.length})
              </Typography>
              {alerts.slice(0, 5).map((a, i) => (
                <Box key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8,
                  background: a.type === 'entry' ? '#dcfce7' : '#fee2e2', marginBottom: 4,
                }}>
                  <NotificationsIcon style={{ fontSize: 14, color: a.type === 'entry' ? '#22c55e' : '#ef4444' }} />
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                    {a.vehicle?.plate} — {a.type === 'entry' ? (pt ? 'Entrou em' : 'Entered') : (pt ? 'Saiu de' : 'Left')} {a.geofence?.name}
                  </Typography>
                  <Typography style={{ fontSize: '0.58rem', color: '#94a3b8' }}>
                    {new Date(a.createdAt).toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Create button */}
          <Button size="small" variant="contained" onClick={() => setShowForm(!showForm)}
            startIcon={<AddIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#3b82f6', color: '#fff', marginBottom: 12 }}>
            {pt ? 'Nova Zona' : 'New Zone'}
          </Button>

          {/* Form */}
          {showForm && (
            <Box style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fafbfc' }}>
              <TextField size="small" variant="outlined" fullWidth label={pt ? 'Nome da zona' : 'Zone name'}
                value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                style={{ marginBottom: 8 }} inputProps={{ style: { fontSize: '0.78rem' } }} />
              <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <TextField size="small" variant="outlined" label="Latitude" type="number"
                  value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' }, step: '0.0001' }} />
                <TextField size="small" variant="outlined" label="Longitude" type="number"
                  value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' }, step: '0.0001' }} />
                <TextField size="small" variant="outlined" label={pt ? 'Raio (m)' : 'Radius (m)'} type="number"
                  value={form.radius} onChange={e => setForm({ ...form, radius: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
              </Box>
              <Box style={{ display: 'flex', gap: 4, marginBottom: 8, alignItems: 'center' }}>
                <Typography style={{ fontSize: '0.68rem', color: '#64748b', marginRight: 4 }}>{pt ? 'Cor:' : 'Color:'}</Typography>
                {COLORS.map(c => (
                  <Box key={c} onClick={() => setForm({ ...form, color: c })} style={{
                    width: 20, height: 20, borderRadius: 6, background: c, cursor: 'pointer',
                    border: form.color === c ? '2px solid #1e293b' : '2px solid transparent',
                  }} />
                ))}
              </Box>
              <Box style={{ display: 'flex', gap: 12, marginBottom: 8 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={form.alertOnEntry} onChange={e => setForm({ ...form, alertOnEntry: e.target.checked })} color="primary" />}
                  label={<Typography style={{ fontSize: '0.68rem' }}>{pt ? 'Alerta entrada' : 'Entry alert'}</Typography>}
                />
                <FormControlLabel
                  control={<Switch size="small" checked={form.alertOnExit} onChange={e => setForm({ ...form, alertOnExit: e.target.checked })} color="primary" />}
                  label={<Typography style={{ fontSize: '0.68rem' }}>{pt ? 'Alerta saída' : 'Exit alert'}</Typography>}
                />
              </Box>
              <Box style={{ display: 'flex', gap: 8 }}>
                <Button size="small" variant="contained" onClick={handleCreate}
                  style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#22c55e', color: '#fff' }}>
                  {pt ? 'Criar' : 'Create'}
                </Button>
                <Button size="small" onClick={() => setShowForm(false)}
                  style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, color: '#64748b' }}>
                  {pt ? 'Cancelar' : 'Cancel'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Geofences list */}
          {geofences.map(g => (
            <Box key={g.id} className={classes.card}>
              <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Box style={{ width: 12, height: 12, borderRadius: '50%', background: g.color || '#3b82f6', flexShrink: 0 }} />
                <Box style={{ flex: 1 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>{g.name}</Typography>
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                    {g.type === 'circle' ? `${pt ? 'Círculo' : 'Circle'} · ${g.radius}m` : pt ? 'Polígono' : 'Polygon'}
                    {g.alertOnEntry ? ` · ${pt ? 'Entrada' : 'Entry'}` : ''}
                    {g.alertOnExit ? ` · ${pt ? 'Saída' : 'Exit'}` : ''}
                    {g.alertCount ? ` · ${g.alertCount} alertas` : ''}
                  </Typography>
                </Box>
                <IconButton size="small" onClick={() => handleDelete(g.id)} style={{ color: '#ef4444' }}>
                  <DeleteIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            </Box>
          ))}

          {geofences.length === 0 && (
            <Box style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
              <GpsFixedIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 8 }} />
              <Typography style={{ fontSize: '0.82rem' }}>
                {pt ? 'Sem zonas geofencing' : 'No geofencing zones'}
              </Typography>
              <Typography style={{ fontSize: '0.68rem', color: '#cbd5e1' }}>
                {pt ? 'Crie zonas para receber alertas de entrada/saída' : 'Create zones to receive entry/exit alerts'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default GeofenceDialog;
